import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api, apiError, eur, deDate } from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { CameraButtons } from "../../components/CameraButtons";
import { Plus, Download, Trash2 } from "lucide-react";

const STATUS = ["entwurf", "eingekauft", "pruefung", "aufbereitung", "lager", "online", "reserviert", "verkauft", "retour", "defekt", "entsorgt"];
const STATUS_LABEL = { entwurf: "Entwurf", eingekauft: "Eingekauft", pruefung: "Prüfung", aufbereitung: "Aufbereitung", lager: "Lager", online: "Online", reserviert: "Reserviert", verkauft: "Verkauft", retour: "Retour", defekt: "Defekt", entsorgt: "Entsorgt" };

export function StatusBadge({ status }) {
  const cls = status === "lager" || status === "online" ? "bg-zinc-900 text-white border-zinc-900"
    : status === "verkauft" ? "bg-zinc-200 text-zinc-700 border-zinc-300"
    : status === "defekt" || status === "entsorgt" ? "bg-red-50 text-red-700 border-red-200"
    : "bg-zinc-100 text-zinc-700 border-zinc-300";
  return <span className={`text-[10px] font-mono px-2 py-0.5 rounded-full border ${cls}`} data-testid={`status-badge-${status}`}>{STATUS_LABEL[status] || status}</span>;
}

export default function Bestand() {
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [filters, setFilters] = useState({ q: "", status: "", category_id: "" });
  const [detail, setDetail] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ product_id: "", name: "", purchase_price: "", sale_price: "", condition: "gut", serial: "", location: "", status: "lager" });
  const [params] = useSearchParams();

  const load = () => {
    const qs = new URLSearchParams(Object.entries(filters).filter(([, v]) => v)).toString();
    api.get(`/articles?${qs}`).then((r) => setArticles(r.data));
  };
  useEffect(() => { load(); }, [filters.status, filters.category_id]);
  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data));
    api.get("/products").then((r) => setProducts(r.data));
  }, []);
  useEffect(() => { if (params.get("neu")) setCreateOpen(true); }, [params]);

  const openDetail = async (id) => {
    const { data } = await api.get(`/articles/${id}`);
    setDetail(data);
  };

  const createArticle = async () => {
    try {
      const p = products.find((x) => x.id === form.product_id);
      await api.post("/articles", { ...form, name: form.name || p?.name, purchase_price: parseFloat(form.purchase_price) || 0, sale_price: parseFloat(form.sale_price) || 0 });
      toast.success("Artikel erstellt.");
      setCreateOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const updateStatus = async (id, status) => {
    await api.put(`/articles/${id}`, { status });
    toast.success(`Status: ${STATUS_LABEL[status]}`);
    load();
    if (detail?.article?.id === id) openDetail(id);
  };

  const addPhotos = async (imgs) => {
    if (!detail?.article) return;
    for (const d of imgs) {
      await api.post(`/articles/${detail.article.id}/photos`, { label: `Foto ${(detail.article.photos?.length || 0) + 1}`, data: d });
    }
    toast.success(`${imgs.length} Foto(s) dem Artikel zugeordnet.`);
    openDetail(detail.article.id);
  };

  const removePhoto = async (photoId) => {
    await api.delete(`/articles/${detail.article.id}/photos/${photoId}`);
    openDetail(detail.article.id);
  };

  const exportCsv = () => { window.open(`${process.env.REACT_APP_BACKEND_URL}/api/export/artikel`, "_blank"); };

  return (
    <div className="space-y-6" data-testid="bestand-page">
      <div className="flex justify-between items-end flex-wrap gap-3">
        <div>
          <div className="text-xs font-mono uppercase tracking-wider text-zinc-400">Artikel</div>
          <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight mt-1">Bestand</h1>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={exportCsv} data-testid="export-articles-button"><Download className="w-4 h-4 mr-1" /> CSV</Button>
          <Button onClick={() => setCreateOpen(true)} className="bg-zinc-900 hover:bg-zinc-800 text-white" data-testid="new-article-button"><Plus className="w-4 h-4 mr-1" /> Neuer Artikel</Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <Input placeholder="Suche: Name, Nummer, IMEI, Seriennr.…" value={filters.q} data-testid="bestand-search"
          onChange={(e) => setFilters({ ...filters, q: e.target.value })} onKeyDown={(e) => e.key === "Enter" && load()} className="w-72" />
        <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} data-testid="bestand-status-filter"
          className="border border-zinc-200 rounded-lg px-3 py-2 text-sm bg-white">
          <option value="">Alle Status</option>
          {STATUS.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
        </select>
        <select value={filters.category_id} onChange={(e) => setFilters({ ...filters, category_id: e.target.value })} data-testid="bestand-category-filter"
          className="border border-zinc-200 rounded-lg px-3 py-2 text-sm bg-white">
          <option value="">Alle Kategorien</option>
          {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <Button variant="outline" onClick={load} data-testid="bestand-apply-filter">Anwenden</Button>
      </div>

      <div className="bg-white border border-zinc-200 rounded-xl shadow-sm overflow-x-auto">
        <table className="w-full text-sm min-w-[760px]">
          <thead><tr className="border-b border-zinc-100 text-left">
            <th className="px-5 py-3 text-[10px] font-mono uppercase tracking-wider text-zinc-400">Nr.</th>
            <th className="px-5 py-3 text-[10px] font-mono uppercase tracking-wider text-zinc-400">Artikel</th>
            <th className="px-5 py-3 text-[10px] font-mono uppercase tracking-wider text-zinc-400">Seriennr./IMEI</th>
            <th className="px-5 py-3 text-[10px] font-mono uppercase tracking-wider text-zinc-400">Status</th>
            <th className="px-5 py-3 text-[10px] font-mono uppercase tracking-wider text-zinc-400 text-right">EK</th>
            <th className="px-5 py-3 text-[10px] font-mono uppercase tracking-wider text-zinc-400 text-right">VK</th>
            <th className="px-5 py-3 text-[10px] font-mono uppercase tracking-wider text-zinc-400 text-right">Marge</th>
            <th className="px-5 py-3"></th>
          </tr></thead>
          <tbody className="divide-y divide-zinc-50">
            {articles.length === 0 && <tr><td colSpan={8} className="px-5 py-8 text-center text-zinc-400">Keine Artikel gefunden.</td></tr>}
            {articles.map((a) => (
              <tr key={a.id} onClick={() => openDetail(a.id)} data-testid={`article-row-${a.number}`} className="hover:bg-zinc-50 cursor-pointer">
                <td className="px-5 py-3 font-mono text-xs">{a.number}</td>
                <td className="px-5 py-3">{a.name} <span className="text-xs text-zinc-400">{a.condition}</span></td>
                <td className="px-5 py-3 font-mono text-xs text-zinc-500">{a.serial || a.imei || "–"}</td>
                <td className="px-5 py-3"><StatusBadge status={a.status} /></td>
                <td className="px-5 py-3 text-right">{eur(a.purchase_price)}</td>
                <td className="px-5 py-3 text-right">{eur(a.sale_price)}</td>
                <td className="px-5 py-3 text-right font-medium">{eur((a.sale_price || 0) - (a.purchase_price || 0))}</td>
                <td className="px-5 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                  {["lager", "online", "reserviert"].includes(a.status) && (
                    <Link to={`/app/verkauf/neu?artikel=${a.id}`}>
                      <Button size="sm" className="bg-zinc-900 hover:bg-zinc-800 text-white min-h-[40px]" data-testid={`article-sell-${a.number}`}>VERKAUFEN</Button>
                    </Link>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!detail} onOpenChange={() => setDetail(null)}>
        <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto" data-testid="article-detail-dialog">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-3">{detail.article.name} <StatusBadge status={detail.article.status} /></DialogTitle>
              </DialogHeader>
              <div className="text-sm space-y-4">
                <div className="grid grid-cols-2 gap-x-6 gap-y-2 font-mono text-xs text-zinc-600">
                  <span>Nr.: {detail.article.number}</span>
                  <span>Seriennr.: {detail.article.serial || "–"}</span>
                  <span>IMEI: {detail.article.imei || "–"}</span>
                  <span>EAN: {detail.article.ean || "–"}</span>
                  <span>Lagerort: {detail.article.location || "–"}</span>
                  <span>Zustand: {detail.article.condition || "–"}</span>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="border border-zinc-100 rounded-lg p-3"><div className="text-[9px] font-mono uppercase text-zinc-400">Einkauf</div><div className="font-semibold">{eur(detail.article.purchase_price)}</div><div className="text-xs text-zinc-400">{deDate(detail.article.purchase_date)}</div></div>
                  <div className="border border-zinc-100 rounded-lg p-3"><div className="text-[9px] font-mono uppercase text-zinc-400">Verkauf</div><div className="font-semibold">{detail.article.status === "verkauft" ? eur(detail.article.sale_price) : "–"}</div><div className="text-xs text-zinc-400">{deDate(detail.article.sale_date)}</div></div>
                  <div className="border border-zinc-100 rounded-lg p-3"><div className="text-[9px] font-mono uppercase text-zinc-400">Gewinn</div><div className="font-semibold">{detail.article.status === "verkauft" ? eur((detail.article.sale_price || 0) - (detail.article.purchase_price || 0)) : "–"}</div></div>
                </div>
                {detail.purchase && <div className="text-xs text-zinc-500">Ankauf: <span className="font-mono">{detail.purchase.number}</span> · Verkäufer: {detail.seller ? `${detail.seller.first_name} ${detail.seller.last_name}` : "–"}</div>}
                {detail.sale && <div className="text-xs text-zinc-500">Verkauf: <span className="font-mono">{detail.sale.number}</span> · Kunde: {detail.customer ? `${detail.customer.first_name} ${detail.customer.last_name}` : "–"}</div>}
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-400 mb-2">Lebenszyklus</div>
                  <div className="space-y-1">
                    {(detail.article.history || []).map((h, i) => (
                      <div key={i} className="text-xs font-mono text-zinc-500 flex justify-between border-b border-zinc-50 pb-1">
                        <span>{h.event}</span><span>{new Date(h.ts).toLocaleString("de-DE")}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-400 mb-2">Fotos ({detail.article.photos?.length || 0})</div>
                  <CameraButtons compact onCapture={addPhotos} />
                  {(detail.article.photos || []).length > 0 && (
                    <div className="grid grid-cols-4 gap-2 mt-3" data-testid="article-photos">
                      {detail.article.photos.map((ph, pi) => (
                        <div key={ph.id || pi} className="relative group">
                          <img src={ph.data} alt={ph.label} className="w-full h-20 object-cover rounded-lg border border-zinc-200" />
                          {ph.id && (
                            <button onClick={() => removePhoto(ph.id)} data-testid={`article-photo-delete-${ph.id}`}
                              className="absolute top-1 right-1 bg-zinc-900/80 text-white rounded-full w-5 h-5 text-[10px] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                              <Trash2 className="w-3 h-3" />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div>
                  <Label>Status ändern</Label>
                  <select value={detail.article.status} onChange={(e) => updateStatus(detail.article.id, e.target.value)} data-testid="article-status-select"
                    className="mt-1.5 w-full border border-zinc-200 rounded-lg px-3 py-2 text-sm bg-white">
                    {STATUS.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
                  </select>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent data-testid="article-create-dialog">
          <DialogHeader><DialogTitle>Neuer Artikel</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Produkt</Label>
              <select value={form.product_id} onChange={(e) => setForm({ ...form, product_id: e.target.value })} data-testid="article-create-product"
                className="mt-1.5 w-full border border-zinc-200 rounded-lg px-3 py-2 text-sm bg-white">
                <option value="">— manuell —</option>
                {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            {!form.product_id && <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="article-create-name" className="mt-1.5" /></div>}
            <div className="grid grid-cols-2 gap-3">
              <div><Label>EK (€)</Label><Input type="number" step="0.01" value={form.purchase_price} onChange={(e) => setForm({ ...form, purchase_price: e.target.value })} data-testid="article-create-ek" className="mt-1.5" /></div>
              <div><Label>VK (€)</Label><Input type="number" step="0.01" value={form.sale_price} onChange={(e) => setForm({ ...form, sale_price: e.target.value })} data-testid="article-create-vk" className="mt-1.5" /></div>
              <div><Label>Seriennr./IMEI</Label><Input value={form.serial} onChange={(e) => setForm({ ...form, serial: e.target.value })} data-testid="article-create-serial" className="mt-1.5 font-mono" /></div>
              <div><Label>Lagerort</Label><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="mt-1.5" /></div>
            </div>
            <Button onClick={createArticle} className="w-full bg-zinc-900 hover:bg-zinc-800 text-white" data-testid="article-create-save">Artikel erstellen</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
